import React from "react";
import { Autocomplete, TextField } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import {
  ScreenOption,
  VIEW_ALL_OPTION,
  applyScreenSelection,
} from "../../utils/viewOnlyAccess";

/**
 * "Visible Screens" multi-select shown under the Access Level radios when
 * View Only is selected (ENG-1815). `value` is the saved screen list where
 * empty = View All; `options` is the backend vocabulary already filtered for
 * the target user (Health only when they have CHM sites).
 */
export const VisibleScreensSelect = ({
  value,
  onChange,
  options,
  disabled = false,
  width = 300,
  loadError = null,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  options: ScreenOption[];
  disabled?: boolean;
  width?: number;
  /** Set when the vocabulary request failed; shown so the empty list is explained. */
  loadError?: string | null;
}) => {
  const selected: ScreenOption[] =
    value.length === 0
      ? [VIEW_ALL_OPTION]
      : value.map(
          (key) =>
            options.find((option) => option.key === key) ?? {
              key,
              label: key,
            }
        );

  // A saved key the vocabulary does not (yet) list — e.g. before the list has
  // loaded — still needs to be an option so the chip renders and can be removed.
  const unlistedSelected = selected.filter(
    (item) =>
      item.key !== VIEW_ALL_OPTION.key &&
      !options.some((option) => option.key === item.key)
  );

  return (
    <div className="pt-[10px]" style={{ width }}>
      <Autocomplete
        multiple
        disablePortal
        disableClearable
        disabled={disabled}
        value={selected}
        options={[VIEW_ALL_OPTION, ...options, ...unlistedSelected]}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, current) => option.key === current.key}
        onChange={(event, next) => {
          onChange(
            applyScreenSelection(
              value,
              next.map((option) => option.key)
            )
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            label="Visible Screens"
            id="visible_screens"
            name="visible_screens"
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.key}>
            {option.label}
          </li>
        )}
      />
      <FormHelperText error={Boolean(loadError)}>
        {loadError
          ? `Screen options could not be loaded (${loadError}). Saved screens still apply.`
          : "Screens this user can open. View All keeps every screen available."}
      </FormHelperText>
    </div>
  );
};
